import { motion } from "framer-motion";
import { Check, Zap, Star } from "lucide-react";
import { Button } from "./ui/button";

const PricingSection = () => {
  const scrollToContact = () => {
    const element = document.getElementById("contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const features = [
    "Load search across multiple boards",
    "Rate negotiation with brokers",
    "Paperwork preparation",
    "WhatsApp support",
    "Backhaul optimization",
    "Priority dispatch support",
    "Advanced load matching",
    "24/7 dedicated support",
    "Broker credit checks",
    "Route optimization",
  ];

  return (
    <section
      id="pricing"
      className="py-20 md:py-32 bg-white"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-4">
            Limited Time Offer
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-primary tracking-tight uppercase mb-6">
            Simple, Transparent Pricing
          </h2>
          <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto">
            No hidden fees. No percentages. Just a flat monthly rate that saves you $2,500-$4,000 every month.
          </p>
        </motion.div>

        {/* Single Pricing Card */}
        <div className="max-w-lg mx-auto mb-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative bg-white rounded-2xl p-8 border-2 border-accent shadow-xl"
            data-testid="pricing-card-pro"
          >
            {/* Popular Badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="bg-accent text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-full flex items-center gap-1">
                <Star className="w-3 h-3 fill-white" />
                Early Bird Discount
              </span>
            </div>

            <div className="text-center mb-8 pt-4">
              <div className="w-16 h-16 rounded-xl bg-accent flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-heading text-3xl font-bold text-primary mb-2">
                Full Service Dispatch
              </h3>
              <p className="text-slate-600">Everything you need to maximize your earnings</p>
            </div>

            <div className="text-center mb-8">
              <div className="flex items-baseline justify-center gap-2">
                <span className="text-2xl font-bold text-slate-400 line-through">$497</span>
                <span className="text-2xl font-bold text-slate-400">$</span>
                <span className="font-heading text-6xl font-bold text-primary">
                  297
                </span>
              </div>
              <p className="text-slate-500 mt-1">per month</p>
              <p className="text-accent font-semibold mt-2">Save $200/month with early bird pricing!</p>
            </div>

            <ul className="space-y-4 mb-8">
              {features.map((feature, featureIndex) => (
                <li key={featureIndex} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-accent flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                  <span className="text-slate-700">{feature}</span>
                </li>
              ))}
            </ul>

            <Button
              onClick={scrollToContact}
              className="w-full bg-accent hover:bg-accent/90 text-white font-bold uppercase tracking-wide py-6 text-lg rounded-md btn-primary"
              data-testid="pricing-cta-pro"
            >
              Join The Waitlist
            </Button>
          </motion.div>
        </div>

        {/* Setup Fee Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center bg-slate-50 rounded-xl p-6 max-w-2xl mx-auto"
          data-testid="setup-fee-notice"
        >
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <div className="text-left">
              <p className="font-heading text-xl font-bold text-primary">One-Time Setup Fee</p>
              <p className="text-slate-600">Includes onboarding and account configuration</p>
            </div>
            <div className="text-right">
              <p className="font-heading text-3xl font-bold text-accent">$300</p>
              <p className="text-slate-500 text-sm">one-time</p>
            </div>
          </div>
        </motion.div>

        {/* FAQ-style points */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 text-center"
        >
          <p className="text-slate-600 max-w-2xl mx-auto">
            <span className="font-semibold">No contracts.</span> Cancel anytime. 
            <span className="font-semibold"> No hidden fees.</span> What you see is what you pay.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
